<template>
  <div class="world-editor gm-page">
    <!-- Top bar -->
    <div class="world-editor-toolbar gm-topbar">
      <div class="toolbar-section mode-controls">
        <div class="mode-switch" role="group" aria-label="Editor mode">
          <button
              :class="['mode-option', { 'is-active': !isEditMode }]"
              :aria-pressed="!isEditMode"
              title="Look around the map. Nothing you click changes it."
              @click="setMode(false)"
          >View</button>
          <button
              :class="['mode-option', { 'is-active': isEditMode }]"
              :aria-pressed="isEditMode"
              title="Place, move, link and delete rooms."
              @click="setMode(true)"
          >Build</button>
        </div>
      </div>

      <!-- SubLocation Selector -->
      <div class="toolbar-section sublocation-controls">
        <label class="gm-label sublocation-label">SubLocation</label>
        <button
          class="sublocation-selector-btn"
          :disabled="isSubLocationsLoading || subLocations.length === 0"
          @click="openSubLocationModal"
        >
          <div v-if="isSubLocationsLoading" class="loading-state">
            Loading…
          </div>
          <div v-else-if="subLocations.length === 0" class="empty-state">
            No sublocations
          </div>
          <div v-else-if="currentSubLocation" class="selected-sublocation">
            <div
              class="sublocation-background"
              :style="{ backgroundImage: currentSubLocation.image ? `url(${currentSubLocation.image})` : 'none' }"
            >
              <div class="sublocation-overlay">
                <span class="sublocation-name">{{ currentSubLocation.name }}</span>
              </div>
            </div>
          </div>
          <div v-else class="no-selection">
            Choose a sublocation
          </div>
        </button>
      </div>

      <div class="toolbar-section action-controls gm-topbar__actions">
        <span v-if="hasUnsavedChanges" class="unsaved-marker" title="This map has changes that are not saved yet">
          Unsaved changes
        </span>
        <button class="gm-btn gm-btn--sm" title="Reload the map from the server" @click="refreshWorld">
          Refresh
        </button>
        <button :disabled="!hasUnsavedChanges" class="gm-btn gm-btn--sm gm-btn--primary" @click="saveWorld">
          Save
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="world-editor-content">
      <!-- Left rail: what you build with, and what the map shows -->
      <aside class="left-panel gm-scroll">
        <WorldEditorToolbar
            v-if="isEditMode"
            :selectedTool="selectedTool"
            class="editor-toolbar"
            @tool-selected="onToolSelected"
            @auto-connect-toggled="onAutoConnectToggled"
        />
        <p v-else class="rail-note">
          <span class="rail-note__label">View mode</span>
          Switch to Build to place, move and link rooms.
        </p>

        <div class="gm-divider"></div>

        <WorldEditorLayersPanel
            :activeLayers="activeLayers"
            @layer-toggled="onLayerToggled"
        />
      </aside>

      <!-- Centre: the map -->
      <div class="center-panel">
        <RiftLoader overlay size="lg"
          v-if="isMapLoading"
          label="Loading map data…"
          note="Please wait while we fetch the map"
        />

        <WorldEditorMap
            ref="worldEditorMap"
            :currentFloor="currentFloor"
            :currentSubLocationId="currentSubLocation?.id || null"
            :editorState="editorState"
            :mapData="mapData"
            :currentCycleNumber="currentCycleNumber"
            class="world-map"
            @room-selected="onRoomSelected"
            @room-created="onRoomCreated"
            @room-moved="onRoomMoved"
            @room-deleted="onRoomDeleted"
            @connection-created="onConnectionCreated"
            @connection-deleted="onConnectionDeleted"
            @connection-selected="onConnectionSelected"
            @map-clicked="onMapClicked"
            @show-entity-details="onShowEntityDetails"
            @layer-toggled="onLayerToggled"
            @refresh-map="refreshWorld"
            @floor-up="floorUp"
            @floor-down="floorDown"
        />
      </div>

      <!-- Right column: the selected room, and the library you drag from -->
      <aside class="right-panel">
        <WorldEditorRoomInfo
            v-if="selectedRoom"
            ref="roomInfoPanel"
            :editable="isEditMode"
            :labelBusy="labelBusy"
            :labelCatalog="labelCatalog"
            :labelConflicts="labelConflicts"
            :removingEntityId="removingEntityId"
            :room="selectedRoom"
            :editorState="editorState"
            class="room-info-panel"
            @close="clearSelection"
            @room-updated="onRoomUpdated"
            @room-deleted="onRoomDeleted"
            @connection-removed="onConnectionDeleted"
            @connection-updated="onConnectionUpdated"
            @room-selected="onRoomSelected"
            @character-selected="openCharacterCard"
            @open-spawner-modal="onOpenSpawnerModal"
            @label-add="onLabelAdd"
            @label-remove="onLabelRemove"
            @label-set="onLabelSet"
            @entity-removed="onEntityRemoved"
            @refresh-room="onRefreshRoom"
        />
        <div v-else class="gm-empty inspector-empty">
          <div class="gm-empty__title">No room selected</div>
          <p class="gm-empty__hint">Click a room on the map to read and edit it.</p>
        </div>

        <!-- Library. Collapsed by default: it is a source you reach for, not
             something that needs to hold a third of the screen. It stays a
             panel rather than a modal because items are dragged from here onto
             the room above. -->
        <section :class="['library', { 'is-open': libraryOpen }]">
          <header class="library-head">
            <button
                class="library-toggle"
                :aria-expanded="libraryOpen"
                :title="libraryOpen ? 'Close the library' : 'Open the library'"
                @click="toggleLibrary"
            >
              <svg class="library-chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 10l4-4 4 4"/></svg>
              <span class="gm-label library-title">Library</span>
            </button>
            <div v-if="libraryOpen" class="gm-tabs library-tabs">
              <button
                  :class="['gm-tab', { 'is-active': libraryTab === 'items' }]"
                  @click="libraryTab = 'items'"
              >Items</button>
              <button
                  :class="['gm-tab', { 'is-active': libraryTab === 'npcs' }]"
                  @click="libraryTab = 'npcs'"
              >NPCs</button>
            </div>
          </header>

          <div v-if="libraryOpen" class="library-body">
            <WorldEditorItemsList
                v-show="libraryTab === 'items'"
                class="items-list"
                @item-selected="onItemSelected"
            />
            <NPCTemplatesList
                v-if="libraryTab === 'npcs'"
                class="npc-templates-list"
                @template-selected="onTemplateSelected"
            />
          </div>
        </section>
      </aside>

      <!-- Character Cards Overlay -->
      <GameMasterCharacterCard
          v-for="characterId in openedCharacterIds"
          :key="characterId"
          :characterId="characterId"
          :style="getCharacterCardStyle(characterId)"
          @close="closeCharacterCard(characterId)"
          @item-selected="onCharacterItemSelected"
      />

      <!-- SubLocation Selector Modal -->
      <div v-if="showSubLocationModal" class="modal-overlay" @click="closeSubLocationModal">
        <div class="modal-content" @click.stop>
          <GMSubLocationSelector
            :subLocations="subLocations"
            :selectedSubLocationId="currentSubLocation?.id"
            @select="onSubLocationSelected"
            @close="closeSubLocationModal"
          />
        </div>
      </div>

      <!-- Spawner Constructor Modal -->
      <RPGSpawnerConstructorModal
          v-if="showSpawnerModal"
          :position-id="spawnerModalData.positionId"
          :npc-spawner-id="spawnerModalData.npcSpawnerId"
          @spawner-created="onSpawnerCreated"
          @spawner-updated="onSpawnerUpdated"
          @close="closeSpawnerModal"
      />
    </div>

    <!-- Status Bar -->
    <div class="world-editor-status">
      <div class="status-left">
        <span class="status-item"><span class="status-figure">{{ totalRooms }}</span> rooms</span>
        <span class="status-sep"></span>
        <span class="status-item"><span class="status-figure">{{ totalConnections }}</span> connections</span>
        <span class="status-sep"></span>
        <span class="status-item"><span class="status-figure">{{ selectedRooms.length }}</span> selected</span>
      </div>
      <div class="status-right">
        <span v-if="lastAction" class="status-item status-action">{{ lastAction }}</span>
        <span class="status-item status-time">{{ currentTime }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {worldEditorService} from '@/services/WorldEditorService.ts';
import {WorldEditorLayer, WorldEditorMode, WorldEditorTool} from '@/models/WorldEditorModels.ts';
import {ensureConnection} from "@/api/dx-websocket/index.ts";
import {ActionGameApi, GMWorldSubLocationsApi} from "@/api/backendService.js";
import ClientManagerService from '@/services/clientManagerService.js';

// Import WorldEditor components (to be created)
import WorldEditorToolbar from '@/components/WorldEditor/WorldEditorToolbar.vue';
import WorldEditorRoomInfo from '@/components/WorldEditor/WorldEditorRoomInfo.vue';
import WorldEditorLayersPanel from '@/components/WorldEditor/WorldEditorLayersPanel.vue';
import WorldEditorMap from '@/components/WorldEditor/WorldEditorMap.vue';
import WorldEditorEntitySpawner from '@/components/WorldEditor/WorldEditorEntitySpawner.vue';
import WorldEditorItemsList from '@/components/WorldEditor/WorldEditorItemsList.vue';
import NPCTemplatesList from '@/components/shared/NPCTemplatesList.vue';
import GameMasterCharacterCard from '@/components/WorldEditor/GameMasterCharacterCard.vue';
import GMSubLocationSelector from '@/components/GameMaster/Character/GMSubLocationSelector.vue';
import RPGSpawnerConstructorModal from '@/components/GameMaster/RPGSpawners/RPGSpawnerConstructorModal.vue';
import RiftLoader from '@/components/loader/RiftLoader.vue';

export default {
  name: 'WorldEditor',
  components: {
    WorldEditorToolbar,
    WorldEditorRoomInfo,
    WorldEditorLayersPanel,
    WorldEditorMap,
    WorldEditorEntitySpawner,
    WorldEditorItemsList,
    NPCTemplatesList,
    GameMasterCharacterCard,
    GMSubLocationSelector,
    RPGSpawnerConstructorModal,
    RiftLoader
  },
  data() {
    return {
      // Service reference
      service: worldEditorService,

      // Editor state
      editorState: null,
      mapData: null,

      // UI state
      selectedRoom: null,
      hasUnsavedChanges: false,
      lastAction: '',
      currentTime: new Date().toLocaleTimeString(),
      libraryOpen: false, // The library starts closed; the map is what matters first
      labelCatalog: null,
      labelConflicts: [],
      labelBusy: false,
      removingEntityId: null,
      libraryTab: 'items',
      autoConnectEnabled: true, // Auto-connect toggle state (default enabled)

      // Timer for current time update
      timeUpdateInterval: null,

      // Current cycle/turn tracking
      currentCycleNumber: null,
      bus: null,

      // Map loading state
      isMapLoading: false,

      // Character cards
      openedCharacterIds: [],

      // SubLocation management
      subLocations: [],
      currentSubLocation: null,
      isSubLocationsLoading: false,
      showSubLocationModal: false,

      // Spawner modal management
      showSpawnerModal: false,
      spawnerModalData: {
        positionId: null,
        campaignId: 'current',
        npcSpawnerId: null
      }
    };
  },
  computed: {
    currentMode() {
      return this.editorState?.mode || WorldEditorMode.VIEW;
    },
    isEditMode() {
      return this.currentMode === WorldEditorMode.EDIT;
    },
    selectedTool() {
      return this.editorState?.selectedTool || WorldEditorTool.SELECT;
    },
    currentFloor() {
      const editorStateFloor = this.editorState?.currentFloor
      if (editorStateFloor !== undefined) {
        return editorStateFloor;
      }
      return 1; // Default to floor 1 if not set
    },
    activeLayers() {
      return this.editorState?.activeLayers || new Set();
    },
    selectedRooms() {
      return Array.from(this.editorState?.selectedRooms || []);
    },
    totalRooms() {
      return this.editorState?.rooms?.size || 0;
    },
    totalConnections() {
      return this.editorState?.connections?.size || 0;
    },
    layerCounts() {
      if (!this.editorState) return {};

      const counts = {};
      const rooms = this.editorState.getRoomsForFloor();

      Object.values(WorldEditorLayer).forEach(layer => {
        counts[layer] = rooms.reduce((count, room) => {
          return count + (room.hasLayerEntities(layer) ? 1 : 0);
        }, 0);
      });

      return counts;
    },
    worldStats() {
      if (!this.editorState) return {};

      const rooms = Array.from(this.editorState.rooms.values());
      return {
        totalRooms: rooms.length,
        totalConnections: this.editorState.connections.size,
        floorsUsed: [...new Set(rooms.map(r => r.position.grid_z))].length,
        entitiesByType: {
          players: rooms.reduce((sum, r) => sum + r.players.length, 0),
          npcs: rooms.reduce((sum, r) => sum + r.npcs.length, 0),
          objects: rooms.reduce((sum, r) => sum + r.objects.length, 0),
          anomalies: rooms.reduce((sum, r) => sum + r.anomalies.length, 0)
        }
      };
    }
  },
  watch: {
    // The conflict report belongs to one room, so it is re-read whenever the
    // selection moves rather than left showing the previous room's problems.
    selectedRoom(room) {
      this.refreshLabelState(room);
    }
  },
  async mounted() {
    try {
      // Initialize the WorldEditor service
      await this.service.initialize();
      this.editorState = this.service.getState();
      this.loadLabelCatalog();

      // Load saved floor from localStorage if available
      const savedFloor = localStorage.getItem('worldEditor_currentFloor');
      if (savedFloor !== null) {
        const floor = parseInt(savedFloor, 10);
        if (!isNaN(floor)) {
          await this.service.setFloor(floor);
        }
      }

      // Fetch SubLocations and restore saved SubLocation
      await this.fetchSubLocations();
      const savedSubLocation = localStorage.getItem('worldEditor_currentSubLocation');
      if (savedSubLocation) {
        try {
          const parsedSubLocation = JSON.parse(savedSubLocation);
          // Verify the saved SubLocation still exists in the fetched list
          const existingSubLocation = this.subLocations.find(sl => sl.id === parsedSubLocation.id);
          if (existingSubLocation) {
            this.currentSubLocation = existingSubLocation;
          }
        } catch (error) {
          console.warn('Failed to parse saved SubLocation:', error);
        }
      }

      // The selected character wins over the restored selection — opening the editor should
      // land on where play actually is.
      await this.alignToCurrentCharacter();

      // Covers the no-character case, where the restored sub-location and floor can disagree.
      await this.followSubLocationToItsFloor(this.currentSubLocation);

      // Set up event listeners
      this.setupEventListeners();

      // Set up keyboard event listeners
      this.setupKeyboardListeners();

      // Start time update interval
      this.timeUpdateInterval = setInterval(() => {
        this.currentTime = new Date().toLocaleTimeString();
      }, 1000);

      // Subscribe to new cycle events
      this.bus = ensureConnection();
      this.bus.on("world::new_cycle", this.handleCycleChange);

      // Get initial cycle number
      await this.fetchCurrentCycle();

      this.setLastAction('WorldEditor initialized');
    } catch (error) {
      console.error('Failed to initialize WorldEditor:', error);
      this.setLastAction('Failed to initialize WorldEditor');
    }
  },
  beforeUnmount() {
    // Clean up event listeners
    this.cleanupEventListeners();

    // Clean up keyboard event listeners
    this.cleanupKeyboardListeners();

    // Clear time update interval
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }

    // Clean up cycle subscription
    if (this.bus) {
      this.bus.off("world::new_cycle", this.handleCycleChange);
    }
  },
  methods: {
    // Cycle/turn handling methods
    async fetchCurrentCycle() {
      try {
        const response = await ActionGameApi.actionCurrentCycleRetrieve();
        this.currentCycleNumber = response.data.number;
      } catch (error) {
        console.error("Failed to fetch current cycle:", error);
      }
    },

    async handleCycleChange(data) {
      console.log("Cycle change event received", data);

      // Only react to cycles in current campaign
      if (!data.is_current) {
        console.debug("Cycle is not current, skipping update");
        return;
      }

      if (data.number === this.currentCycleNumber) {
        console.debug("Cycle number is the same, skipping update");
        return;
      }
      this.currentCycleNumber = data.number;

      // Refresh world data when cycle changes
      await this.refreshWorld();
    },

    // SubLocation management methods
    async fetchSubLocations() {
      try {
        this.isSubLocationsLoading = true;
        const response = await GMWorldSubLocationsApi.gamemasterWorldSubLocationsList(true); // Only active SubLocations
        this.subLocations = response.data.results || response.data || [];

        // Set the first active SubLocation as current if none is selected
        if (!this.currentSubLocation && this.subLocations.length > 0) {
          this.currentSubLocation = this.subLocations[0];
        }
      } catch (error) {
        console.error("Failed to fetch SubLocations:", error);
        this.subLocations = [];
      } finally {
        this.isSubLocationsLoading = false;
      }
    },

    async alignToCurrentCharacter() {
      try {
        const clientInfo = await ClientManagerService.getCurrentClientInfo();
        const positionId = clientInfo?.main_character?.position_id;
        if (!positionId) {
          return;
        }

        // initialize() already loaded the whole graph, so the character's position is
        // in state — no extra round trip is needed to resolve its floor and sub-location.
        const room = this.editorState?.rooms?.get(positionId);
        if (!room) {
          console.warn(`Character position ${positionId} is not present in the loaded world graph`);
          return;
        }

        if (room.position.grid_z !== this.currentFloor) {
          await this.service.setFloor(room.position.grid_z);
        }

        if (room.subLocationId) {
          const characterSubLocation = this.subLocations.find(sl => sl.id === room.subLocationId);
          if (characterSubLocation) {
            this.selectSubLocation(characterSubLocation);
          }
        }

        await this.$nextTick();
        this.$refs.worldEditorMap?.centerOnRoom(room);

        this.setLastAction(`Aligned to ${clientInfo.main_character.name || 'current character'}`);
      } catch (error) {
        console.warn('Could not align world editor to the current character:', error);
      }
    },

    selectSubLocation(subLocation) {
      this.currentSubLocation = subLocation;
      this.setLastAction(`Selected SubLocation: ${subLocation.name}`);
      // Save selected SubLocation to localStorage
      localStorage.setItem('worldEditor_currentSubLocation', JSON.stringify(subLocation));
    },

    /**
     * A sub-location's rooms may not live on the floor currently in view, which would leave the
     * map blank. Stay put if this floor has rooms, otherwise drop to the one that holds most.
     */
    async followSubLocationToItsFloor(subLocation) {
      if (!subLocation) return;

      const roomsPerFloor = new Map();
      this.editorState?.rooms?.forEach(room => {
        if (room.subLocationId === subLocation.id) {
          const floor = room.position.grid_z;
          roomsPerFloor.set(floor, (roomsPerFloor.get(floor) || 0) + 1);
        }
      });

      if (roomsPerFloor.size === 0 || roomsPerFloor.has(this.currentFloor)) {
        return;
      }

      const [bestFloor] = [...roomsPerFloor.entries()].sort((a, b) => b[1] - a[1])[0];
      await this.service.setFloor(bestFloor);
    },

    // SubLocation modal methods
    openSubLocationModal() {
      this.showSubLocationModal = true;
    },

    closeSubLocationModal() {
      this.showSubLocationModal = false;
    },

    async onSubLocationSelected(subLocation) {
      this.selectSubLocation(subLocation);
      await this.followSubLocationToItsFloor(subLocation);
      this.closeSubLocationModal();
    },

    // Spawner modal methods
    openSpawnerModal(positionId, npcSpawnerId = null) {
      this.spawnerModalData = {
        positionId: positionId,
        campaignId: 'current', // This should be dynamically set based on current campaign
        npcSpawnerId: npcSpawnerId
      };
      this.showSpawnerModal = true;
    },

    closeSpawnerModal() {
      this.showSpawnerModal = false;
      this.spawnerModalData = {
        positionId: null,
        campaignId: 'current',
        npcSpawnerId: null
      };
    },

    async onSpawnerCreated(spawnerId) {
      console.log('Spawner created:', spawnerId);
      this.closeSpawnerModal();
      this.setLastAction('Spawner created successfully');
      // Refresh world data to show the new spawner
      await this.refreshWorld();
    },

    async onSpawnerUpdated(spawnerId) {
      console.log('Spawner updated:', spawnerId);
      this.closeSpawnerModal();
      this.setLastAction('Spawner updated successfully');
      // Refresh world data to show the updated spawner
      await this.refreshWorld();
    },

    // Event handlers for spawner-related events from WorldEditorRoomInfo
    onOpenSpawnerModal(data) {
      const { positionId, npcSpawnerId } = data;
      this.openSpawnerModal(positionId, npcSpawnerId);
    },

    async onRefreshRoom() {
      // Refresh world data to update room information
      await this.refreshWorld();
    },

    setupEventListeners() {
      this.service.on('stateUpdated', this.onStateUpdated);
      this.service.on('roomCreated', this.onRoomCreatedEvent);
      this.service.on('roomDeleted', this.onRoomDeletedEvent);
      this.service.on('roomUpdated', this.onRoomUpdatedEvent);
      this.service.on('connectionCreated', this.onConnectionCreatedEvent);
      this.service.on('connectionDeleted', this.onConnectionDeletedEvent);
      this.service.on('modeChanged', this.onModeChanged);
      this.service.on('toolChanged', this.onToolChanged);
      this.service.on('layerToggled', this.onLayerToggledEvent);
    },

    cleanupEventListeners() {
      this.service.off('stateUpdated', this.onStateUpdated);
      this.service.off('roomCreated', this.onRoomCreatedEvent);
      this.service.off('roomDeleted', this.onRoomDeletedEvent);
      this.service.off('roomUpdated', this.onRoomUpdatedEvent);
      this.service.off('connectionCreated', this.onConnectionCreatedEvent);
      this.service.off('connectionDeleted', this.onConnectionDeletedEvent);
      this.service.off('modeChanged', this.onModeChanged);
      this.service.off('toolChanged', this.onToolChanged);
      this.service.off('layerToggled', this.onLayerToggledEvent);
    },

    // Keyboard event listeners
    setupKeyboardListeners() {
      document.addEventListener('keydown', this.handleKeyDown);
    },

    cleanupKeyboardListeners() {
      document.removeEventListener('keydown', this.handleKeyDown);
    },

    handleKeyDown(event) {
      // Handle Escape key to cancel in-progress actions
      if (event.key === 'Escape') {
        this.handleEscapeKey();
      }
    },

    handleEscapeKey() {
      // Cancel any in-progress actions in the map component
      if (this.$refs.worldEditorMap) {
        this.$refs.worldEditorMap.cancelInProgressActions();
      }

      // Provide user feedback
      this.setLastAction('Action canceled');
    },

    // Event handlers
    onStateUpdated(state) {
      this.editorState = state;
      this.hasUnsavedChanges = true;
      // Force Vue to detect changes in the state object
      this.$forceUpdate();
    },

    onRoomCreatedEvent(room) {
      this.setLastAction(`Created room at (${room.position.grid_x}, ${room.position.grid_y})`);
    },

    onRoomDeletedEvent({roomId, room}) {
      this.setLastAction(`Deleted room ${room?.getDisplayName() || roomId}`);
      if (this.selectedRoom?.id === roomId) {
        this.selectedRoom = null;
      }
    },

    onRoomUpdatedEvent(room) {
      this.setLastAction(`Updated room ${room.getDisplayName()}`);
      if (this.selectedRoom?.id === room.id) {
        this.selectedRoom = room;
      }
    },

    onConnectionCreatedEvent(connection) {
      this.setLastAction('Created connection between rooms');
    },

    onConnectionDeletedEvent({connectionId}) {
      this.setLastAction('Deleted connection');
    },

    onModeChanged(mode) {
      this.setLastAction(`Switched to ${mode.toUpperCase()} mode`);
    },

    onToolChanged(tool) {
      this.setLastAction(`Selected ${tool.replace('_', ' ')} tool`);
    },

    onLayerToggledEvent({layer, active}) {
      this.setLastAction(`${active ? 'Enabled' : 'Disabled'} ${layer} layer`);
    },

    // UI Actions
    toggleMode() {
      this.service.toggleMode();
    },

    setMode(edit) {
      if (edit !== this.isEditMode) {
        this.service.toggleMode();
      }
    },

    toggleLibrary() {
      this.libraryOpen = !this.libraryOpen;
      this.setLastAction(this.libraryOpen ? 'Library opened' : 'Library closed');
    },

    onToolSelected(tool) {
      this.service.setTool(tool);
    },

    onAutoConnectToggled(enabled) {
      this.autoConnectEnabled = enabled;
      this.setLastAction(`Auto-connect ${enabled ? 'enabled' : 'disabled'}`);
    },

    onLayerToggled(layer) {
      this.service.toggleLayer(layer);
    },

    async floorUp() {
      const newFloor = this.currentFloor + 1;
      await this.service.setFloor(newFloor);
      // Save current floor to localStorage
      localStorage.setItem('worldEditor_currentFloor', newFloor.toString());
      this.setLastAction(`Moved to floor ${newFloor}`);
    },

    async floorDown() {
      const newFloor = this.currentFloor - 1;
      await this.service.setFloor(newFloor);
      // Save current floor to localStorage
      localStorage.setItem('worldEditor_currentFloor', newFloor.toString());
      this.setLastAction(`Moved to floor ${newFloor}`);
    },

    async refreshWorld() {
      try {
        this.isMapLoading = true;
        await this.service.refresh();
        this.setLastAction('World data refreshed');
        this.hasUnsavedChanges = false;
      } catch (error) {
        console.error('Failed to refresh world:', error);
        this.setLastAction('Failed to refresh world data');
      } finally {
        this.isMapLoading = false;
      }
    },

    async saveWorld() {
      try {
        // This would implement saving logic
        this.setLastAction('World saved successfully');
        this.hasUnsavedChanges = false;
      } catch (error) {
        console.error('Failed to save world:', error);
        this.setLastAction('Failed to save world');
      }
    },

    // Map event handlers
    onRoomSelected(room, shiftKey = false) {
      // Handle spawner creation if CREATE_SPAWNER tool is active
      if (this.currentTool === WorldEditorTool.CREATE_SPAWNER) {
        this.onCreateSpawnerInRoom(room);
        return;
      }

      this.selectedRoom = room;

      // If shift key is not pressed, clear selection before selecting the new room
      if (!shiftKey) {
        this.service.clearSelection();
      }

      this.service.toggleRoomSelection(room.id);
    },

    async onRoomCreated(position) {
      try {
        const subLocationId = this.currentSubLocation ? this.currentSubLocation.id : null;
        const previouslySelectedRoom = this.selectedRoom;

        const newRoom = await this.service.createRoom(position.gridX, position.gridY, position.gridZ, subLocationId);

        // Automatically select the newly created room (Issue #2)
        if (newRoom) {
          this.selectedRoom = newRoom;
          this.service.clearSelection();
          this.service.toggleRoomSelection(newRoom.id);

          // Create automatic connection if auto-connect is enabled and there was a previously selected room
          if (this.autoConnectEnabled && previouslySelectedRoom && previouslySelectedRoom.id !== newRoom.id) {
            try {
              await this.service.createConnection(previouslySelectedRoom.id, newRoom.id, false);
              this.setLastAction(`Created room and auto-connected to ${previouslySelectedRoom.getDisplayName?.() || 'previous room'}`);
            } catch (connectionError) {
              console.error('Failed to create automatic connection:', connectionError);
              this.setLastAction('Created room but failed to create automatic connection');
            }
          } else {
            this.setLastAction('Created room');
          }
        }
      } catch (error) {
        console.error('Failed to create room:', error);
        this.setLastAction('Failed to create room');
      }
    },

    async onCreateSpawnerInRoom(room) {
      try {
        // For now, use a default character template ID
        // In a real implementation, this should open a dialog to select character template
        const defaultCharacterTemplateId = '0c772e55-920d-4e7f-918a-89fb01f32b76'; // From the example in issue description

        const newSpawner = await this.service.createSpawner(room.id, defaultCharacterTemplateId);

        if (newSpawner) {
          this.setLastAction(`Created spawner in ${room.getDisplayName?.() || 'room'}`);

          // Optionally switch back to select tool after creating spawner
          this.service.setTool(WorldEditorTool.SELECT);
        }
      } catch (error) {
        console.error('Failed to create spawner:', error);
        this.setLastAction('Failed to create spawner');
      }
    },

    async onRoomMoved(roomId, newPosition) {
      try {
        await this.service.moveRoom(roomId, newPosition.gridX, newPosition.gridY, newPosition.gridZ);
      } catch (error) {
        console.error('Failed to move room:', error);
        this.setLastAction('Failed to move room');
      }
    },

    async onConnectionCreated(fromRoomId, toRoomId, isVertical = false) {
      try {
        await this.service.createConnection(fromRoomId, toRoomId, isVertical);
      } catch (error) {
        console.error('Failed to create connection:', error);
        this.setLastAction('Failed to create connection');
      }
    },

    async onConnectionDeleted(connectionId) {
      try {
        await this.service.deleteConnection(connectionId);
      } catch (error) {
        console.error('Failed to delete connection:', error);
        this.setLastAction('Failed to delete connection');
      }
    },

    onConnectionSelected(connection) {
      // Find the source room for this connection
      const fromRoom = this.editorState.rooms.get(connection.fromRoomId);
      if (fromRoom) {
        // Select the room
        this.selectedRoom = fromRoom;
        // Clear selection before selecting the new room (no shift key for connection selection)
        this.service.clearSelection();
        this.service.toggleRoomSelection(fromRoom.id);

        // Use nextTick to ensure the room info component is mounted
        this.$nextTick(() => {
          // Access the room info component via ref and open the connection info
          if (this.$refs.roomInfoPanel) {
            this.$refs.roomInfoPanel.openConnectionInfo(connection);
          }
        });

        this.setLastAction('Connection selected');
      }
    },

    async onConnectionUpdated(connectionId, updates) {
      try {
        await this.service.updateConnection(connectionId, updates);
        this.setLastAction('Connection updated');
      } catch (error) {
        console.error('Failed to update connection:', error);
        this.setLastAction('Failed to update connection');
      }
    },

    onMapClicked(position) {
      if (this.isEditMode && this.selectedTool === WorldEditorTool.CREATE_ROOM) {
        this.onRoomCreated(position);
      }
    },

    // Room event handlers
    async onRoomUpdated(room, updates) {
      try {
        await this.service.updateRoom(room.id, updates);
      } catch (error) {
        console.error('Failed to update room:', error);
        this.setLastAction('Failed to update room');
      }
    },

    // Label event handlers. Every write goes to the server first: the server
    // decides which slot a label belongs in, and answers with the conflicts it
    // found, so the panel never shows a state the database does not hold.
    async onLabelAdd(room, value) {
      await this.runLabelChange(
          () => this.service.addRoomLabel(room.id, value),
          `Added label "${value}"`
      );
    },

    async onLabelRemove(room, value) {
      await this.runLabelChange(
          () => this.service.removeRoomLabel(room.id, value),
          `Removed label "${value}"`
      );
    },

    async onLabelSet(room, parts) {
      await this.runLabelChange(
          () => this.service.setRoomLabels(room.id, parts),
          'Labels updated'
      );
    },

    async runLabelChange(operation, successMessage) {
      this.labelBusy = true;
      try {
        const state = await operation();
        this.labelConflicts = state.conflicts || [];
        this.setLastAction(successMessage);
      } catch (error) {
        const detail = error?.response?.data?.value?.[0];
        console.error('Failed to change labels:', error);
        this.setLastAction(detail || 'Failed to change labels');
      } finally {
        this.labelBusy = false;
      }
    },

    async onEntityRemoved(room, objectId, entity) {
      if (!objectId) {
        this.setLastAction('That object has no id to remove');
        return;
      }
      const name = entity?.displayName || entity?.name || 'object';
      this.removingEntityId = objectId;
      try {
        await this.service.removeGameObject(room.id, objectId);
        this.setLastAction(`Removed ${name} from ${room.getDisplayName?.() || 'the room'}`);
      } catch (error) {
        console.error('Failed to remove the object:', error);
        this.setLastAction(`Failed to remove ${name}`);
      } finally {
        this.removingEntityId = null;
      }
    },

    async loadLabelCatalog() {
      try {
        this.labelCatalog = await this.service.getLabelCatalog();
      } catch (error) {
        console.error('Failed to load the label catalog:', error);
      }
    },

    async refreshLabelState(room) {
      if (!room) {
        this.labelConflicts = [];
        return;
      }
      try {
        const state = await this.service.getRoomLabels(room.id);
        this.labelConflicts = state.conflicts || [];
      } catch (error) {
        console.error('Failed to read labels:', error);
        this.labelConflicts = [];
      }
    },

    async onRoomDeleted(room) {
      try {
        await this.service.deleteRoom(room.id);
      } catch (error) {
        console.error('Failed to delete room:', error);
        this.setLastAction('Failed to delete room');
      }
    },

    // Entity event handlers
    async onEntitySpawned(entityType, entityData) {
      if (!this.selectedRoom) return;

      try {
        switch (entityType) {
          case 'item':
            await this.service.spawnItem(this.selectedRoom.id, entityData);
            break;
          case 'npc':
            await this.service.spawnNPC(this.selectedRoom.id, entityData);
            break;
          case 'anomaly':
            await this.service.spawnAnomaly(this.selectedRoom.id, entityData);
            break;
        }
      } catch (error) {
        console.error(`Failed to spawn ${entityType}:`, error);
        this.setLastAction(`Failed to spawn ${entityType}`);
      }
    },

    // Utility methods
    clearSelection() {
      this.selectedRoom = null;
      this.service.clearSelection();
    },

    setLastAction(action) {
      this.lastAction = action;
      // Clear the action after 5 seconds
      setTimeout(() => {
        if (this.lastAction === action) {
          this.lastAction = '';
        }
      }, 5000);
    },

    /**
     * Handle item selection from the items list
     */
    onItemSelected(item) {
      console.log('Item selected:', item);
      this.setLastAction(`Selected item: ${item.name}`);

      // If we're in edit mode and have a selected room, spawn the item
      if (this.isEditMode && this.selectedRoom) {
        this.onEntitySpawned('item', item);
      }
    },


    /**
     * Open a character card for the given character ID
     * @param {string} characterId - The ID of the character to open
     */
    openCharacterCard(characterId) {
      if (!characterId) return;

      // Check if character is already opened
      if (this.openedCharacterIds.includes(characterId)) {
        // Move to front if already opened
        this.openedCharacterIds = this.openedCharacterIds.filter(id => id !== characterId);
        this.openedCharacterIds.unshift(characterId);
      } else {
        // Add to opened characters, maintaining max of 2
        this.openedCharacterIds.unshift(characterId);
        if (this.openedCharacterIds.length > 2) {
          this.openedCharacterIds.pop();
        }
      }

      this.setLastAction(`Character card opened: ${characterId}`);
    },

    /**
     * Close a character card
     * @param {string} characterId - The ID of the character to close
     */
    closeCharacterCard(characterId) {
      this.openedCharacterIds = this.openedCharacterIds.filter(id => id !== characterId);
      this.setLastAction(`Character card closed: ${characterId}`);
    },

    /**
     * Get the style for a character card based on its position in the openedCharacterIds array
     * @param {string} characterId - The ID of the character
     * @returns {Object} - The style object for the character card
     */
    getCharacterCardStyle(characterId) {
      const index = this.openedCharacterIds.indexOf(characterId);
      if (index === -1) return {};

      // Only set z-index to allow free movement in any direction
      return {
        zIndex: 1000 - index
      };
    },

    /**
     * Handle item selection from a character card
     * @param {Object} item - The selected item
     */
    onCharacterItemSelected(item) {
      console.log('Character item selected:', item);
      this.setLastAction(`Selected character item: ${item.name}`);

      // If we're in edit mode and have a selected room, spawn the item
      if (this.isEditMode && this.selectedRoom) {
        this.onEntitySpawned('item', item);
      }
    },

    /**
     * Handle template selection from the NPC templates list
     * @param {Object} template - The selected NPC template
     */
    onTemplateSelected(template) {
      console.log('NPC template selected:', template);
      this.setLastAction(`Selected NPC template: ${template.name}`);

      // If we're in edit mode and have a selected room, spawn the NPC from the template
      if (this.isEditMode && this.selectedRoom) {
        this.spawnNPCFromTemplate(template, this.selectedRoom);
      }
    },

    /**
     * Spawn an NPC from a template in the specified room
     * @param {Object} template - The NPC template
     * @param {Object} room - The room to spawn the NPC in
     */
    async spawnNPCFromTemplate(template, room) {
      try {
        console.log(`Spawning NPC from template ${template.id} in room ${room.id}`);

        // Create position object for the NPC
        const position = {
          id: room.id,
          grid_x: room.position.grid_x,
          grid_y: room.position.grid_y,
          grid_z: room.position.grid_z
        };

        // Use the character templates service to create the NPC
        const { characterTemplatesService } = await import('@/services/CharacterTemplatesService.ts');
        const npc = await characterTemplatesService.createNpcFromTemplate(template.id, position);

        this.setLastAction(`Created NPC from template: ${template.name}`);

        // Refresh the world to show the new NPC
        await this.refreshWorld();

        return npc;
      } catch (error) {
        console.error('Failed to spawn NPC from template:', error);
        this.setLastAction(`Failed to create NPC from template: ${error.message || 'Unknown error'}`);
        throw error;
      }
    },

    /**
     * Handle showing entity details, including opening character cards for characters
     */
    onShowEntityDetails(details) {
      // Handle showing entity details
      // This could open a modal, update a sidebar, etc.
      const entityTypeName = {
        'players': 'Players',
        'npcs': 'NPCs',
        'objects': 'Objects',
        'anomalies': 'Anomalies'
      }[details.entityType] || 'Entities';

      const count = details.entities.length;
      this.setLastAction(`${entityTypeName} in room: ${count}`);

      // Select the room
      if (details.roomId) {
        const room = this.editorState.rooms.get(details.roomId);
        if (room) {
          this.selectedRoom = room;
          // Clear selection before selecting the new room (no shift key for entity details)
          this.service.clearSelection();
          this.service.toggleRoomSelection(room.id);

          // If the entity type is players or npcs, open character cards for them
          if ((details.entityType === 'players' || details.entityType === 'npcs') && details.entities.length > 0) {
            // Open character cards for up to 2 characters
            const charactersToOpen = details.entities.slice(0, 2);
            charactersToOpen.forEach(character => {
              if (character.id) {
                this.openCharacterCard(character.id);
              }
            });
          }
        }
      }
    },

  }
};
</script>

<style scoped>
/* The editor fills its shell exactly and never grows the document. Every region
   that can overflow scrolls inside itself, so the page as a whole does not. */
/* The editor is taken out of flow and stretched over <main>, which is
   position: relative and already sized by the shell's flex column. In flow it
   sized the row, the row sized the panels, and the panels sized themselves from
   their content — so a long room inspector grew the document by a thousand
   pixels. Out of flow, <main> gets its height from the viewport and the editor
   gets its height from <main>: every panel then has a real ceiling to scroll
   inside. */
.world-editor {
  position: absolute;
  inset: 0;
  min-height: 0;
  overflow: hidden;
}

.toolbar-section {
  display: flex;
  align-items: center;
  gap: var(--gm-gap);
}

.world-editor-toolbar {
  flex: none;
}

/* ---------- mode switch ---------- */

/* One control with two states, rather than a button that renames itself next to
   a badge that repeats it. */
.mode-switch {
  display: inline-flex;
  padding: 2px;
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
}

.mode-option {
  padding: 0.32rem 0.9rem;
  background: transparent;
  border: none;
  border-radius: calc(var(--gm-radius-sm) - 1px);
  color: var(--gm-text-muted);
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-control);
  font-weight: 600;
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease), background var(--gm-duration) var(--gm-ease);
}

.mode-option:hover {
  color: var(--gm-text);
}

.mode-option:focus-visible {
  outline: 1px solid var(--gm-border-hi);
  outline-offset: 1px;
}

.mode-option.is-active {
  background: var(--gm-accent);
  color: var(--gm-text-on-accent);
}

.unsaved-marker {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  font-weight: 600;
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  color: var(--gm-accent);
}

/* ---------- three-column body ---------- */

.world-editor-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.left-panel {
  flex: none;
  width: 240px;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
  padding: var(--gm-gap);
  background: var(--gm-surface-1);
  border-right: 1px solid var(--gm-border-dim);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.rail-note {
  margin: 0;
  padding: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border-left: 2px solid var(--gm-border-dim);
  border-radius: 0 var(--gm-radius-sm) var(--gm-radius-sm) 0;
  font-size: var(--gm-text-xs);
  line-height: var(--gm-leading-tight);
  color: var(--gm-text-muted);
}

.rail-note__label {
  display: block;
  margin-bottom: 0.15rem;
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  font-weight: 600;
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  color: var(--gm-text-dim);
}

.center-panel {
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
}

.world-map {
  width: 100%;
  height: 100%;
}

.right-panel {
  flex: none;
  width: 330px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--gm-surface-1);
  border-left: 1px solid var(--gm-border-dim);
  overflow: hidden;
}

/* Inspector and library split the column evenly with a floor each, so opening
   the library never squeezes the room you are dropping onto out of reach. */
.room-info-panel {
  flex: 1 1 0;
  min-height: 220px;
}

.inspector-empty {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--gm-gap-xs);
  padding: var(--gm-gap-lg);
  text-align: center;
}

/* ---------- library drawer ---------- */

.library {
  flex: none;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-top: 1px solid var(--gm-border-dim);
  background: var(--gm-surface-1);
}

.library.is-open {
  flex: 1 1 0;
  min-height: 260px;
}



.library-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap-xs) var(--gm-gap-sm);
}

.library-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--gm-gap-xs);
  padding: 0.25rem 0.2rem;
  background: none;
  border: 1px solid transparent;
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
}

.library-toggle:hover .library-title {
  color: var(--gm-accent);
}

.library-toggle:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.library-title {
  margin: 0;
}

.library-chevron {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: var(--gm-text-dim);
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
  transform: rotate(180deg);
  transition: transform var(--gm-duration) var(--gm-ease);
}

.library.is-open .library-chevron {
  transform: rotate(0deg);
}

.library-tabs {
  border-bottom: none;
}

.library-body {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.items-list,
.npc-templates-list {
  flex: 1;
  min-height: 0;
}

/* The drawer's tab already names the list. */
.library-body :deep(.items-list-header > .gm-section-title),
.library-body :deep(.templates-list-header > .gm-section-title) {
  display: none;
}

/* ---------- status bar ---------- */

.world-editor-status {
  flex: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--gm-gap);
  padding: var(--gm-gap-xs) var(--gm-gap-lg);
  padding-bottom: calc(var(--gm-gap-xs) + var(--gm-dock-clearance));
  background: var(--gm-surface-1);
  border-top: 1px solid var(--gm-border-dim);
  font-size: var(--gm-text-xs);
}

.status-left, .status-right {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  min-width: 0;
}

.status-item {
  color: var(--gm-text-muted);
  white-space: nowrap;
}

.status-figure {
  color: var(--gm-text);
  font-family: var(--gm-font-mono);
  font-variant-numeric: tabular-nums;
}

.status-sep {
  width: 1px;
  height: 0.8em;
  background: var(--gm-border-dim);
}

.status-action {
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-time {
  font-family: var(--gm-font-mono);
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

/* ---------- responsive ---------- */

@media (max-width: 1400px) {
  .right-panel {
    width: 300px;
  }
}

@media (max-width: 1100px) {
  .left-panel {
    width: 200px;
  }

  .right-panel {
    width: 270px;
  }
}

@media (max-width: 860px) {
  .world-editor-content {
    flex-direction: column;
  }

  .left-panel {
    width: 100%;
    max-height: 180px;
    border-right: none;
    border-bottom: 1px solid var(--gm-border-dim);
  }

  .center-panel {
    flex: 1 1 50%;
    min-height: 260px;
  }

  .right-panel {
    width: 100%;
    flex: 1 1 40%;
    border-left: none;
    border-top: 1px solid var(--gm-border-dim);
  }

  /* The desktop floors do not fit a stacked column; let both regions shrink. */
  .room-info-panel,
  .library.is-open {
    min-height: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mode-option,
  .library-chevron {
    transition: none;
  }
}

/* SubLocation Selector Button */
.sublocation-selector-btn {
  min-width: 200px;
  height: 44px;
  padding: 0;
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: border-color 0.15s ease;
  overflow: hidden;
  position: relative;
}

.sublocation-selector-btn:hover:not(:disabled) {
  border-color: var(--gm-border);
}

.sublocation-selector-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.loading-state,
.empty-state,
.no-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--gm-text-muted);
  font-size: 0.82rem;
}

.selected-sublocation {
  height: 100%;
  width: 100%;
}

.sublocation-background {
  height: 100%;
  width: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--gm-surface-2);
  position: relative;
}

.sublocation-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 0.3rem 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sublocation-name {
  color: var(--gm-accent);
  font-family: var(--gm-font-display);
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  width: 90vw;
  max-width: 1200px;
  height: 80vh;
  max-height: 600px;
  background: transparent;
  border-radius: var(--gm-radius-lg);
  overflow: hidden;
}
</style>
